import { useQuery } from '@tanstack/react-query';
import { Briefcase } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { groupsOf, type AccessGroup } from '@/api/auth';
import { personQuery } from '@/api/people';
import { useSession } from '@/app/session';
import { ApiErrorState } from '@/components/states/ApiErrorState';
import { Avatar } from '@/components/ui/Avatar';
import { EmptyState } from '@/components/ui/EmptyState';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { cn } from '@/lib/cn';
import NotFound from '@/pages/NotFound';
import { type } from '@/theme/typography';

import { CardMenu } from './CardMenu';
import { onDateTime } from './cardDates';
import { ConsiderationBlock } from './ConsiderationBlock';
import { PersonCardSkeleton } from './PersonCardSkeleton';
import { PossibleDuplicates } from './PossibleDuplicates';
import { SummaryRow } from './SummaryRow';

/**
 * `DESIGN.md` §6.5 — **the most-read screen in the product**, and the first place another person's
 * unsubmitted feedback could leak (**feedback protection #3**, ADR 0006; §7's table).
 *
 * **One request builds the whole card.** `GET /api/v1/people/{id}/` carries the person, the summary
 * row, every consideration and every interview row, and the screen asks for nothing else — §6.5 names
 * the edit somebody will attempt (*"fetch the full interview list once and filter in the component.
 * Do not."*) and `personCard.test.tsx` asserts the only paths this screen requests are that one and
 * the session. The hiding lives in the queryset
 * (`apps.interviews.services.card_interviews`, `apps/interviews/tests/test_blind_feedback.py`), so a
 * blinded interview arrives as a row with no contents at all; there is nothing here to filter, and a
 * client-side filter would leave the data in the response one devtools tab away.
 *
 * ⚠️ **The card is viewer-dependent and the cache key says so** (`queryKeys.person(viewerId,
 * personId)`): `average_score`, `latest_recommendation` and every interview row's figures are
 * computed over the caller's own blind-filtered set, so two viewers hold two entries for one URL and
 * **no placeholder may cross that key** — Sprint 8 shipped a correct key and then walked around it
 * with `placeholderData: keepPreviousData`, which resolves from the observer's last payload under
 * *any* key and reports `status: 'success'`. There is no `placeholderData`, no `select` memoised
 * across keys and no accumulator on this screen; `personQuery`'s docblock and two tests hold it there.
 *
 * ⚠️ **A refusal is 404, never 403** (backend TASK-029): an Interviewer reaching past their
 * assignment gets byte-for-byte what a nonexistent id gets, because a 403 would confirm the candidate
 * exists to somebody with no business knowing. This is a **record** route, so `ApiError.path` already
 * matches `isCandidateScopedPath()` and `ApiErrorState` renders §8's Forbidden state for it. This
 * screen is the first in the product to exercise that mapping rather than assert it in advance, and it
 * adds no second mapping of its own.
 */
export default function PersonCard() {
  const { personId } = useParams();
  const session = useSession();

  /**
   * ⚠️ **A hand-typed `/candidates/ada` names no record, so nothing is requested for it.**
   * `:personId` matches any segment, and `Number('ada')` is `NaN` — `/api/v1/people/NaN/` would 404,
   * miss `CANDIDATE_SCOPED_RECORD` (which needs `\d+`) and land on §8's *error* state, so a typo
   * would read as "something went wrong". *"There is nothing at this address."* is the honest answer
   * and it is the one the rest of the app already gives (`NotFound`, `access.test.tsx`).
   */
  if (personId === undefined || !/^\d+$/.test(personId)) return <NotFound />;

  /**
   * ⚠️ **No viewer, no query key.** A disabled query still reads the cache at its key, so a
   * placeholder id would be one shared bucket for every session-less render of a payload that is
   * viewer-dependent (ADR 0006). The component that builds the key does not exist until there is a
   * viewer; until then this is §8's loading state, which is what it would render anyway.
   */
  if (!session.data) return <PersonCardSkeleton />;

  return (
    <Card
      viewerId={session.data.user.id}
      /**
       * §5's rule reaches this screen too: what a group may not do is **not rendered** here rather
       * than disabled (`CardMenu`, `StatusActions`). It decides rendering only — every write is
       * refused by the server on its own, four rows of `test_permission_matrix.py`.
       */
      groups={groupsOf(session.data)}
      personId={Number(personId)}
    />
  );
}

function Card({
  viewerId,
  groups,
  personId,
}: {
  viewerId: number;
  groups: AccessGroup[];
  personId: number;
}) {
  const { t, i18n } = useTranslation('candidates');
  const card = useQuery(personQuery(viewerId, personId));

  if (card.isPending) return <PersonCardSkeleton />;

  if (card.isError) {
    /**
     * One mapping for every failure (`ApiErrorState`): offline, forbidden, throttled, or the screen's
     * own sentence. A **404 here is the Forbidden state** — this is a record route — and the way back
     * is `/candidates`, because §8 states are not dead ends.
     *
     * ⚠️ The `h1` stays. A failed screen with no heading at all is the defect Sprint 8 found in a
     * live browser on the dashboard: a screen-reader user hears one sentence and a button with
     * nothing saying which screen failed. The name is what the failed request would have carried, so
     * the heading is the screen's own word for what this address is about.
     */
    return (
      <div className="max-w-3xl">
        <h1 className={type.pageTitle}>{t('card.title')}</h1>
        <div className="mt-10">
          <ApiErrorState
            error={card.error}
            message={t('card.error')}
            onRetry={() => void card.refetch()}
            action={<BackToCandidates />}
          />
        </div>
      </div>
    );
  }

  const person = card.data;
  /**
   * ⚠️ **The card is the one place that knows *when*, and it uses the field rather than an
   * inference.** `anonymize_person()` blanks the name, the contacts and the source in place (ADR
   * 0009) — §6.3's row can only infer it from a blank `person_full_name` because
   * `ConsiderationTableRowOut` carries no `anonymized_at`, and `PersonCardOut` does. The blank name
   * is still handled below, so a blank `h1` is unrepresentable either way.
   */
  const anonymized = person.anonymized_at !== null;
  const displayName = person.full_name.trim() === '' ? t('anonymized') : person.full_name;
  const contacts = [person.email, person.phone].filter((contact) => contact !== '');

  return (
    /* §6.5: `max-w-3xl`. A card is read, not scanned, and a name and three figures stretched across
       a wide monitor is the shape of a table rather than of a record. */
    <div className="max-w-3xl">
      <BackToCandidates />

      <header className="mt-2 flex items-start gap-4">
        <Avatar name={displayName} className="size-12 text-sm" />
        {/*
         * ⚠️ **`[overflow-wrap:anywhere]` is what keeps one long contact from moving the page**, and
         * it is the same fix `Table` carries for the same reason (Sprint 8's live pass). Measured in
         * Chrome on the built bundle at 390 px, with a 36-character single-word name and a
         * 52-character email: `documentElement.scrollWidth` **509** against a `clientWidth` of
         * **390** — the whole document scrolled sideways, and under mobile emulation the page was
         * zoomed out to 509 px to fit it. `min-w-0` alone is not enough: it lets the flex item
         * shrink, but a value with no break opportunity still sets the min-content width and paints
         * past the box. `anywhere` rather than `break-words` is the whole point — only `anywhere`
         * counts the break opportunities toward the intrinsic minimum, which is the number the
         * layout is sized from. It sits here, on the block that holds the name, the contacts and the
         * source, because `overflow-wrap` inherits.
         */}
        <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
          {/* §9: the screen's one `h1`, and on a record screen it is the record — a screen-reader
              user who lands here is told whose card this is before anything else. */}
          <h1 className={cn(type.pageTitle, anonymized && 'text-ink-500')}>{displayName}</h1>

          {/* §6.5: *"Avatar, name, contacts, source."* An empty line is never rendered — a
              recruiter often has only one of the two contacts, and an anonymised person has
              neither (they are cleared, not hidden). */}
          {contacts.length > 0 && <p className={cn(type.body, 'mt-1')}>{contacts.join(' · ')}</p>}
          {person.source !== '' && <p className={cn(type.metadata, 'mt-1')}>{person.source}</p>}

          {/*
           * ⚠️ **Where the record came from, which is not where the candidate came from** (TASK-179,
           * ADR 0020, ADR 0021). The line above is `source` — the recruiter's free text, *"Referral
           * (Tom)"* — and this one is `external_source`. They sit together and must not read as one
           * fact: `source` is **personal data** that an erasure clears, this is **provenance** that an
           * erasure deliberately keeps, and two fields that behave oppositely under erasure are told
           * apart here by saying *imported*, which is about the card rather than about the human.
           *
           * ⚠️ **It stands on an erased card, with the four personal fields blank around it.** That
           * is the decision and not an oversight: `anonymize_person` keeps `external_source` on
           * purpose, because it is the tombstone a later import reads to recognise its own work and
           * refuse to re-populate the card — *"cleared, the import could not recognise its own work,
           * would create a fresh card for the same human and would re-populate it from ADP"*. So the
           * reader of an erased imported card is told the one thing that explains why ADP will not
           * quietly undo the erasure, and nothing about the person. It discloses nothing: a card that
           * says *Anonymized* and *Imported from ADP* says this record was erased and came from
           * there, which is what the banner below already says the first half of.
           *
           * **An absent source means Panel's own** (`ExternalSource` in the document), so this is a
           * question about one column and every card that existed before Sprint 24 renders exactly
           * what it rendered before. It is the value and never `=== 'ADP'`: a second integration adds
           * a member to that enum, not a branch here.
           *
           * ⚠️ **No sentence stands beside it, unlike §6.8's.** There, *"Maintained in ADP, so its
           * details are not edited here"* explains a **missing control** — `PATCH
           * /api/v1/roles/{role_id}/` answers 409 on an imported row for every caller. Nothing of the
           * sort is true of a person: `PATCH /api/v1/people/{person_id}/` refuses nothing on
           * provenance grounds, *Edit* is offered here exactly as on any other card, and a sentence
           * saying otherwise would be false in both halves.
           */}
          {person.external_source !== '' && (
            <p className={cn(type.metadata, 'mt-1')}>
              {t('card.imported.from', { source: person.external_source })}
            </p>
          )}
        </div>

        {/*
         * §6.5: *"Overflow menu: Edit, and for Admin, Anonymize."* It renders nothing at all for a
         * viewer with no item in it — an Interviewer, or an anonymised person for whom Edit is a
         * control the server refuses (`CardMenu`).
         */}
        <CardMenu person={person} groups={groups} />
      </header>

      {/*
       * ⚠️ **ADR 0009's state, rendered so that what survived does not read as data loss.** The
       * erasure clears the name, the contacts, the source, **the text of every comment** and
       * everything ADP sent about this candidate — every imported comment, and every imported
       * document, erased from storage (ADR 0020 decision 3); the ratings, the verdicts,
       * `submitted_at`, the frozen question wordings, every consideration and the whole status
       * history **stay**, and that survival is the ADR's entire argument — an erasure that took the
       * panel's record with it would be a deletion by another name. The banner says *when*, which is
       * the only way a reader can tell *"nothing was ever entered"* from *"erased on request"*.
       *
       * ⚠️ **The banner named the card's own fields and the comments, and nothing ADP sent, until
       * 2026-10-28.** The imported comment and the imported document went into the confirmation's
       * *Cleared:* list in the sprint that built them and not into this sentence — and the
       * confirmation is read **once, before the act**, while this is what a reader of an erased card
       * meets for ever. An incomplete account of the one act with no undo belongs in neither, and
       * least of all in the one that outlives it.
       *
       * The **action** is TASK-067's (Admin only, a confirmation `Dialog`, and it is irreversible);
       * this is the state that action leaves behind, and it is reachable today without any action of
       * ours — a person anonymised through the API or the admin.
       */}
      {person.anonymized_at !== null && (
        <p className="mt-4 rounded border border-rule-control bg-paper-sunken p-3 text-sm text-ink-700">
          {t('card.anonymizedOn', { date: onDateTime(person.anonymized_at, i18n.language) })}
        </p>
      )}

      {/*
       * ⚠️ **ADR 0021's compensating surface, and the decision is only honest because it is here.**
       * An ADP import creates a **second card** rather than merging when a candidate matches an
       * existing one — a duplicate is additive, a merge destroys the distinction it assumed — and
       * what pays for that is a recruiter *seeing* the warning where the work happens. It is empty on
       * every card a human created, and it sits above the summary row because it is a fact about the
       * card rather than about one run: everything below it may be a fraction of this person's
       * history.
       */}
      <PossibleDuplicates person={person} groups={groups} />

      <SummaryRow person={person} />

      <section className="mt-10" aria-label={t('card.considerations.label')}>
        <Eyebrow count={person.considerations.length}>{t('card.considerations.label')}</Eyebrow>

        {person.considerations.length === 0 ? (
          <NoConsiderations />
        ) : (
          /* In the order the API sends them — newest first. Nothing is re-sorted here. */
          <ul className="flex flex-col gap-4">
            {person.considerations.map((consideration) => (
              <ConsiderationBlock
                key={consideration.id}
                consideration={consideration}
                personId={person.id}
                viewerId={viewerId}
                groups={groups}
                /* ADR 0009 closes the scheduling write path with a 409, so the block does not offer
                   it — the same gate `CardMenu` puts on *Edit*, from the same field. */
                anonymized={anonymized}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * §8's empty state for this screen: a person with **no run at all**, which is a real state and not a
 * defensive branch — §6.4's partial failure creates exactly it (*"…was created, but the vacancy was
 * not added. Until a vacancy is added, this candidate appears in no row of the list."*), and the
 * duplicate warning links here from the dialog that said so.
 *
 * ⚠️ **A sentence and no action**, which is the same choice §6.3 makes for an Interviewer's empty
 * screen: nothing in the product opens a run from this screen — §6.5 specifies no such control, and
 * `POST /people/{id}/considerations/` is reached from §6.4's dialog — so a button here would be one
 * that cannot work. §8's *"one action"* is not satisfied by inventing one.
 */
function NoConsiderations() {
  const { t } = useTranslation('candidates');

  return <EmptyState icon={Briefcase} message={t('card.considerations.none')} />;
}

/**
 * The way back to §6.3, in the loaded card and as the Forbidden state's action (§8: a state is not a
 * dead end). It is a real `<a>` rather than a `navigate()` button so the list can be opened in a new
 * tab from a card — and so the *"every rendered `href` resolves against `buildRoutes()`"* guard has
 * something to check on a screen whose interview rows deliberately carry no link yet.
 */
function BackToCandidates() {
  const { t } = useTranslation('candidates');

  return (
    <Link
      to="/candidates"
      className="rounded text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {t('card.backToList')}
    </Link>
  );
}
